/* eslint-disable jsx-a11y/anchor-is-valid */
import {plural, t} from "@lingui/core/macro";
import * as React from "react";
import {findDOMNode} from "react-dom";
import _ from "underscore";

import Button from "@khanacademy/wonder-blocks-button";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {Errors, KAError} from "@repo/logging";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import ClearFlagsModal from "#discussion-package/components/clear-flags-modal.tsx";
import {PERFORM_MOD_ACTION} from "#discussion-package/graphql/perform_mod_action.ts";
import $ from "#jquery/jquery-with-qtip.js";

import getFlagsHovercardTemplate from "./flags-hovercard.handlebars.ts";

type FlagsOwnProps = {
    flaggedBy: Array<string | null | undefined> | null | undefined;
    scratchpadKey: string;
    flags: Array<string>;
    inQueue: boolean;
    definitelyNotSpam: boolean;

    // If true, instead of a "Clear # flags" button, we show a "Review # flags"
    // button that opens a modal to review the flags and potentially clear them.
    // This is more accessible and less accident-prone than the default UX,
    // which has a "Clear # flags" button that clears the flags immediately, and
    // the only way to view the flags is to hover over the button. Once the
    // strings are translated, we can start setting this to true from more
    // components that render it, and eventually remove this prop and default to
    // true.
    shouldUseReviewModal?: boolean;
    buttonKind?: React.ComponentProps<typeof Button>["kind"];
};
type FlagsProps = FlagsOwnProps & WithApolloProps;
type FlagsState = {
    status: "not-cleared" | "clearing" | "cleared";
};

/* Counts the flags on this item and displays them when clicked.
 * Clears the flags when clicked.
 * TODO(joel): keyboard shortcuts.
 */
class _Flags extends React.Component<FlagsProps, FlagsState> {
    state: FlagsState = {status: "not-cleared"};

    handleClearFlags = async () => {
        this.setState({status: "clearing"});

        const {gqlClient, scratchpadKey} = this.props;
        const key: string = scratchpadKey;
        const clearFlagsAction = "CLEAR_FLAGS";
        try {
            const response = await gqlClient.mutate(PERFORM_MOD_ACTION, {
                variables: {
                    modAction: clearFlagsAction,
                    entityKey: key,
                },
            });
            const error = response?.data?.performModAction?.error?.code;
            this.setState({status: error == null ? "cleared" : "not-cleared"});
        } catch (e) {
            this.setState({status: "not-cleared"});
        }
    };

    handleMouseEnter = () => {
        const flaggedBy = this.props.flaggedBy;
        const flagTypes = this.props.flags;
        let flags: Array<{
            user?: string | null;
            type: string;
        }> = [];

        // flaggedBy is only passed down to moderators,
        // so we fallback to user-less list of flags otherwise.
        if (flaggedBy) {
            _.each(flaggedBy, function (user, index) {
                flags.push({
                    user: user,
                    type: flagTypes[index],
                });
            });
        } else {
            flags = _.map(flagTypes, function (flagType) {
                return {type: flagType};
            });
        }

        ($(findDOMNode(this) as any) as any).qtip({
            content: {
                text: getFlagsHovercardTemplate()({
                    flags: flags,
                    arrowOnRight: true,
                }),
            },
            style: {
                classes: "custom-override flags-hovercard",
            },
            hide: {
                delay: 100,
                fixed: true,
            },
            position: {
                my: "top right",
                at: "bottom right",
            },
        });
    };

    render(): React.ReactNode {
        const {inQueue, flags, flaggedBy, shouldUseReviewModal} = this.props;
        const buttonKind = this.props.buttonKind ?? "tertiary";

        if (this.state.status === "cleared") {
            return (
                <Button kind="tertiary" disabled>
                    {t({
                        context:
                            "A message indicating to an admin that they have cleared the user-submitted complaints about some content.",
                        message: "Cleared flags",
                    })}
                </Button>
            );
        }
        if (flags?.length) {
            if (shouldUseReviewModal) {
                return (
                    <ReviewFlagsButton
                        flags={flags}
                        flaggedBy={flaggedBy}
                        onClear={this.handleClearFlags}
                        spinner={this.state.status === "clearing"}
                        kind={buttonKind}
                    />
                );
            }
            let text;
            if (inQueue) {
                text = plural(flags.length, {
                    one: "(C)lear # flag",
                    other: "(C)lear # flags",
                });
            } else {
                text = plural(flags.length, {
                    one: "Clear # flag",
                    other: "Clear # flags",
                });
            }
            return (
                // NOTE(Jhead): this mouseEnter handle is not accessible. Tried
                // adding an onFocus handler to both a (non wb) button and the
                // span, but neither is sufficient for triggering the behavior.
                // I suspect the popover is inaccessible as well.
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
                <span onMouseEnter={this.handleMouseEnter}>
                    <Button
                        onClick={this.handleClearFlags}
                        className="clear-flags"
                        color="destructive"
                        kind={buttonKind}
                        spinner={this.state.status === "clearing"}
                    >
                        {text}
                    </Button>
                </span>
            );
        }
        if (inQueue) {
            return (
                <a href="javascript:void(0);" className="approve">
                    {t`(A)pprove`}
                </a>
            );
        }
        throw new KAError(
            "Flags was rendered when it has nothing to show",
            Errors.Internal,
        );
    }
}

type ExportFlagsProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof _Flags,
        React.ComponentProps<typeof _Flags>
    >
>;
export const Flags: React.ComponentType<ExportFlagsProps> = withApollo(_Flags);

type ClearFlagsControlsProps = {
    flaggedBy: Array<string | null | undefined> | null | undefined;
    scratchpadKey: string;
    flags: Array<string>;
    inQueue: boolean;
    definitelyNotSpam: boolean;
};

/* The controls shown to moderators both in discussions on the site and in the
 * mod queue. When shown in the mod queue these controls also have keyboard
 * shortcuts. These are the rightmost controls in the left-floating section of
 * discussion-meta.
 */
// TODO(joel)
//   - break this down more
//   - refactor naturally now that it's not constrained by handlebars style
//   - highlightDeleteButton
export class ClearFlagsControls extends React.Component<ClearFlagsControlsProps> {
    render(): React.ReactNode {
        if (
            !this.props.definitelyNotSpam &&
            (this.props.flags.length || this.props.inQueue)
        ) {
            return (
                <span className="discussion-meta">
                    <span className="discussion-meta-controls">
                        <Flags {...this.props} />
                    </span>
                </span>
            );
        }
        return <span />;
    }
}

function ReviewFlagsButton({
    flags,
    flaggedBy,
    onClear,
    spinner,
    kind,
}: {
    flags: Array<string>;
    flaggedBy: Array<string | null | undefined> | null | undefined;
    onClear: () => void;
    spinner?: boolean;
    kind?: React.ComponentProps<typeof Button>["kind"];
}): React.ReactElement {
    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <ClearFlagsModal
                    flags={flags}
                    flaggedBy={flaggedBy}
                    onCancel={closeModal}
                    onClearAllFlags={() => {
                        onClear();
                        closeModal();
                    }}
                />
            )}
        >
            {({openModal}) => (
                <Button
                    onClick={openModal}
                    className="clear-flags"
                    color="destructive"
                    kind={kind}
                    spinner={spinner}
                >
                    {t({
                        context:
                            "The name of an action to review details provided by users when flagging (or marking) content as being in violation of the community guidelines.",

                        message: plural(flags.length, {
                            one: "Review # flag",
                            other: "Review # flags",
                        }),
                    })}
                </Button>
            )}
        </ModalLauncher>
    );
}
